import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocDiscardEvent } from '@common/collab/events/collab-doc-discard.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { DocTimelineUpdate } from '@common/collab/types/doc-timeline-update.type';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { CollabSession, CollabSessionOptions, CollabSessionTransactionTooLargeEvent, CollabSessionUpdateEvent } from '@common/sockets/collab-session';
import { CollabSessionSyncState } from '@common/sockets/enums/collab-session-sync-state.enum';
import { CollabSessionErrorEvent } from '@common/sockets/types/collab-session-error-event.type';
import { getSchemaByParams } from '@common/util/schema-set';
import { SubscriptionProperty } from '@common/util/subscription-property.decorator';
import { environment } from '@env/environment';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { DataService } from '@portal-core/data/common/services/data.service';
import { FileSizeService } from '@portal-core/general/services/file-size.service';
import { OnlineService } from '@portal-core/general/services/online.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { EditorContextMenuEvent, TextEditorComponent } from '@portal-core/text-editor/components/text-editor/text-editor.component';
import { CollabSessionEventType } from '@portal-core/text-editor/enums/collab-session-event-type.enum';
import { CollabSessionEvent } from '@portal-core/text-editor/types/collab-session-event.type';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { EditorState, Plugin } from 'prosemirror-state';
import { DirectEditorProps, EditorView, NodeViewConstructor } from 'prosemirror-view';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

/**
 * A text editor that connects to the collaborative server for editing files.
 */
@Component({
  selector: 'mc-collab-file-text-editor',
  templateUrl: './collab-file-text-editor.component.html',
  styleUrls: ['./collab-file-text-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class CollabFileTextEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(TextEditorComponent, { static: true }) private textEditor: TextEditorComponent;

  @Input() branchName: string;
  /** Whether the collab session can receive document updates. For example, it can be paused when the component is inactive or hidden. */
  @Input() canReceiveUpdates: boolean = true;
  @Input() commitId: string;
  @Input() fileId: number;
  @Input() filePath: string;
  @Input() fileType: CollabFileType;
  @Input() getTimelineDocVersion?: CollabSessionOptions['getTimelineDocVersion'];
  @Input() licenseUser: LicenseUser;
  @Input() nodeViews: Dictionary<NodeViewConstructor>;
  @Input() plugins: Plugin[];
  @Input() projectId: number;
  @Input() projectLanguage: string;
  @Input() readonly?: boolean = false;
  @Input() schema: CollabSchema;
  @Input() timelineEnabled?: boolean = false;
  @Input() userDataEnabled?: boolean = true;

  @InputObservable('branchName') private branchName$: Observable<string>;
  @InputObservable('canReceiveUpdates') canReceiveUpdates$: Observable<boolean>;
  @InputObservable('commitId') private commitId$: Observable<string>;
  @InputObservable('fileId') private fileId$: Observable<number>;
  @InputObservable('filePath') private filePath$: Observable<string>;
  @InputObservable('fileType') private fileType$: Observable<CollabFileType>;
  @InputObservable('licenseUser') private licenseUser$: Observable<LicenseUser>;
  @InputObservable('projectId') private projectId$: Observable<number>;
  @InputObservable('nodeViews') private nodeViews$: Observable<Dictionary<NodeViewConstructor>>;
  @InputObservable('plugins') private plugins$: Observable<Plugin[]>;

  @Output() contentChange: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() collabSessionEnd: EventEmitter<CollabSessionEvent> = new EventEmitter<CollabSessionEvent>();
  @Output() collabSessionStart: EventEmitter<CollabSessionEvent> = new EventEmitter<CollabSessionEvent>();
  @Output() docCommit: EventEmitter<CollabDocCommitEvent> = new EventEmitter<CollabDocCommitEvent>();
  @Output() docDelete: EventEmitter<CollabDocDeleteEvent> = new EventEmitter<CollabDocDeleteEvent>();
  @Output() docDiscard: EventEmitter<CollabDocDiscardEvent> = new EventEmitter<CollabDocDiscardEvent>();
  @Output() docMerge: EventEmitter<CollabDocMergeEvent> = new EventEmitter<CollabDocMergeEvent>();
  @Output() docRename: EventEmitter<CollabDocRenameEvent> = new EventEmitter<CollabDocRenameEvent>();
  @Output() docRestore: EventEmitter<CollabDocRestoreEvent> = new EventEmitter<CollabDocRestoreEvent>();
  @Output() editorContextMenu: EventEmitter<EditorContextMenuEvent> = new EventEmitter<EditorContextMenuEvent>();
  @Output() editorStateChange: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() errorChange: EventEmitter<CollabSessionErrorEvent> = new EventEmitter<CollabSessionErrorEvent>();
  @Output() exceedsMaxSizeChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() exceedsMongoDBMaxSizeChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() load: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() offlineChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() readonlyChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() ready: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() selectionChange: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() sizeChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() syncStateChange: EventEmitter<CollabSessionSyncState> = new EventEmitter<CollabSessionSyncState>();
  @Output() timelineUpdate: EventEmitter<DocTimelineUpdate> = new EventEmitter<DocTimelineUpdate>();
  @Output() unload: EventEmitter<void> = new EventEmitter<void>();
  @Output() usersUpdate: EventEmitter<CollabUserJSON[]> = new EventEmitter<CollabUserJSON[]>();
  @Output() docCommittedAtChange: EventEmitter<ISO8601DateString> = new EventEmitter<ISO8601DateString>();
  @Output() docModifiedAtChange: EventEmitter<ISO8601DateString> = new EventEmitter<ISO8601DateString>();
  @Output() blockChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  @InputObservable('readonly') readonlyInput$: Observable<boolean>;

  @SubscriptionProperty() private collabSessionDocCommitSubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocDeleteSubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocDiscardSubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocMergeSubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocRenameSubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocRestoreSubscription: Subscription;
  @SubscriptionProperty() private collabSessionErrorSubscription: Subscription;
  @SubscriptionProperty() private collabSessionExceedsMaxSizeSubscription: Subscription;
  @SubscriptionProperty() private collabSessionExceedsMongoDBMaxSizeSubscription: Subscription;
  @SubscriptionProperty() private collabSessionOfflineSubscription: Subscription;
  @SubscriptionProperty() private collabSessionReadonlySubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocSizeSubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocCommittedAtSubscription: Subscription;
  @SubscriptionProperty() private collabSessionDocModifiedAtSubscription: Subscription;
  @SubscriptionProperty() private collabSessionSyncStateSubscription: Subscription;
  @SubscriptionProperty() private collabSessionTimelineUpdateSubscription: Subscription;
  @SubscriptionProperty() private collabSessionUsersUpdateSubscription: Subscription;
  @SubscriptionProperty() private blockChangesSubscription: Subscription;

  private canReceiveUpdatesSubscription: Subscription;
  private collabSessionSubscription: Subscription;
  private collabSession: CollabSession;
  private onlineSubscription: Subscription;
  private reloadDataSource: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  private reload$: Observable<void> = this.reloadDataSource.asObservable();

  editorProps: Partial<DirectEditorProps> = {};
  readonly$: Observable<boolean>;

  get docId(): string {
    return this.collabSession?.docId;
  }

  get docVersion(): number {
    return this.collabSession?.docVersion;
  }

  get docCreatedAt(): ISO8601DateString | undefined {
    return this.collabSession?.docCreatedAt;
  }

  get editorState(): EditorState {
    return this.textEditor ? this.textEditor.editorState : null;
  }

  get editorView(): EditorView {
    return this.textEditor ? this.textEditor.editorView : null;
  }

  get viewPluginOverlay(): HTMLElement {
    return this.textEditor?.viewPluginOverlay;
  }

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private dataService: DataService,
    private fileSizeService: FileSizeService,
    private ngZone: NgZone,
    private onlineService: OnlineService,
    private snackBar: MatSnackBar
  ) { }

  /**
   * Initializes the component by configuring the editor for a collaborative session.
   * Also sets up a listener for keeping track of the online status of the user.
   */
  ngOnInit() {
    this.editorProps.dispatchTransaction = (transaction) => {
      if (this.collabSession) {
        this.collabSession.dispatch(transaction);
      }
    };

    // Keep the collab session up to date on the online status of the user
    this.onlineSubscription = this.onlineService.online$.subscribe(online => {
      if (this.collabSession) {
        this.collabSession.offline = !online;
      }
    });

    // Update the canReceiveUpdates property of the collab session
    this.canReceiveUpdatesSubscription = this.canReceiveUpdates$.pipe(
      distinctUntilChanged(),
    ).subscribe(canReceiveUpdates => {
      if (this.collabSession) {
        this.collabSession.canReceiveUpdates = canReceiveUpdates;
      }
    });
  }

  /**
   * Makes an observable subscription for creating and destroying the collaborative session based on the component's Inputs.
   * This is done in ngAfterViewInit instead of ngOnInit because mc-text-editor is ready to be used here but not in ngOnInit.
   */
  ngAfterViewInit() {
    // Subscribe in ngAfterViewInit because mc-text-editor is now ready to be used
    this.collabSessionSubscription = combineLatest([
      // We only care about a license user change if the license user has changed to a different license user
      this.licenseUser$.pipe(
        filter(licenseUser => !!licenseUser),
        distinctUntilChanged(this.dataService.sameIdentity)
      ),
      this.branchName$, this.fileId$, this.filePath$, this.fileType$, this.projectId$, this.plugins$,
      this.reload$
    ]).pipe(
      debounceTime(5) // Debounce because these properties can change at the same time and we don't want to run through the pipe for every single one.
    ).subscribe(([licenseUser, branchName, fileId, filePath, fileType, projectId, plugins]) => {
      // Only review files have a file id
      const hasValidId = fileType === CollabFileType.Review ? Number.isInteger(fileId) : true;

      const fileSchema = getSchemaByParams(fileId, filePath);
      // Check if the editor schema and file schema are the same
      // the file schema and the editor schema may not match when the selected file is changed.
      const hasValidSchema = fileSchema && fileSchema.name === this.schema.name;

      // If all the necessary properties exist then create a collab session
      if (licenseUser && branchName && hasValidId && filePath && fileType && Number.isInteger(projectId) && plugins && hasValidSchema) {
        this.createCollabSession();
      } else { // Else destroy a collab session if it exist
        this.destroyCollabSession();
      }
    });
  }

  /**
   * Cleans up the component by destroying the collaborative session.
   */
  ngOnDestroy() {
    this.destroyCollabSession();
  }

  onEditorContextMenu(editorEvent: EditorContextMenuEvent) {
    this.editorContextMenu.emit(editorEvent);
  }

  /**
   * Gets the content of the editor as a string.
   * @returns The content of the editor as a string.
   */
  getContent(): string {
    return this.schema.nodeToCode(this.editorState.doc);
  }

  /**
   * Sets the dirty state of the ChangePlugin to false.
   * Any subsequent change events will only be dirty if the user makes a new change.
   * */
  markAsPristine() {
    this.textEditor.markAsPristine();
  }

  /**
   * Forces an existing collaborative session to be destroyed and a new one created.
   * A new session is created only if the required Inputs are provided.
   */
  reload() {
    this.reloadDataSource.next(null);
  }

  /**
   * Stops a collaborative session if it exists.
   * Cleans up all resources and unsubscribes from all events associated with the session.
   */
  private destroyCollabSession() {
    if (this.collabSession) {
      this.collabSessionDocCommitSubscription = null;
      this.collabSessionDocDeleteSubscription = null;
      this.collabSessionDocMergeSubscription = null;
      this.collabSessionDocRenameSubscription = null;
      this.collabSessionDocRestoreSubscription = null;
      this.collabSessionErrorSubscription = null;
      this.collabSessionExceedsMaxSizeSubscription = null;
      this.collabSessionExceedsMongoDBMaxSizeSubscription = null;
      this.collabSessionOfflineSubscription = null;
      this.collabSessionReadonlySubscription = null;
      this.collabSessionDocSizeSubscription = null;
      this.collabSessionDocCommittedAtSubscription = null;
      this.collabSessionDocModifiedAtSubscription = null;
      this.collabSessionSyncStateSubscription = null;
      this.collabSessionTimelineUpdateSubscription = null;
      this.collabSessionUsersUpdateSubscription = null;
      this.blockChangesSubscription = null;

      this.collabSession.stop();
      this.collabSession = null;
      this.collabSessionEnd.emit({
        session: null,
        type: CollabSessionEventType.End
      });
    }

    this.unload.emit();
  }

  /**
   * Creates a new connection to the collaborative server.
   * If an existing session exists it will be destroyed first.
   */
  private createCollabSession() {
    // Stop an existing collab session
    this.destroyCollabSession();

    // Create a new collab session
    this.collabSession = new CollabSession({
      socketBaseUrl: this.apiService.collabAuthorityUrl,
      offline: !this.onlineService.online,
      refreshToken$: () => this.authService.refreshToken$(),
      branchName: this.branchName,
      canReceiveUpdates: this.canReceiveUpdates,
      clientId: uuidv4(),
      editorState: this.textEditor.editorState,
      getTimelineDocVersion: this.getTimelineDocVersion,
      reviewFileId: this.fileId,
      filePath: this.filePath,
      fileType: this.fileType,
      plugins: this.plugins,
      projectId: this.projectId,
      schema: this.schema,
      socketMaxHttpBufferSizeBytes: environment.collabSocketMaxHttpBufferSizeBytes ? parseInt(environment.collabSocketMaxHttpBufferSizeBytes, 10) : undefined,
      timelineEnabled: this.timelineEnabled,
      userId: this.licenseUser?.User?.Id ?? null,
      userName: this.licenseUser?.User?.FullName ?? null,
      userDataEnabled: this.userDataEnabled
    });

    this.collabSession.on('load', () => this.load.emit(true));
    this.collabSession.on('update', (event: CollabSessionUpdateEvent) => this.textEditor.editorView.updateState(event.session.editorState));
    this.collabSession.on('transactionTooLarge', event => {
      this.ngZone.run(() => {
        this.showTransactionTooLargeError(event);
      });
    });

    this.collabSessionDocCommitSubscription = this.collabSession.docCommit$.subscribe(event => this.docCommit.emit(event));
    this.collabSessionDocDeleteSubscription = this.collabSession.docDelete$.subscribe(event => this.docDelete.emit(event));
    this.collabSessionDocDiscardSubscription = this.collabSession.docDiscard$.subscribe(event => this.docDiscard.emit(event));
    this.collabSessionDocMergeSubscription = this.collabSession.docMerge$.subscribe(event => this.docMerge.emit(event));
    this.collabSessionDocRenameSubscription = this.collabSession.docRename$.subscribe(event => this.docRename.emit(event));
    this.collabSessionDocRestoreSubscription = this.collabSession.docRestore$.subscribe(event => this.docRestore.emit(event));
    this.collabSessionErrorSubscription = this.collabSession.error$.subscribe(error => {
      this.errorChange.emit(error)
      if (error)
        this.load.emit(false);
    });
    this.collabSessionExceedsMaxSizeSubscription = this.collabSession.exceedsMaxSize$.subscribe(exceedsMaxSize => this.exceedsMaxSizeChange.emit(exceedsMaxSize));
    this.collabSessionExceedsMongoDBMaxSizeSubscription = this.collabSession.exceedsMongoDBMaxSize$.subscribe(exceedsMongoDBMaxSize => this.exceedsMongoDBMaxSizeChange.emit(exceedsMongoDBMaxSize));
    this.collabSessionOfflineSubscription = this.collabSession.offline$.subscribe(offline => this.offlineChange.emit(offline));
    this.collabSessionReadonlySubscription = this.collabSession.readOnly$.subscribe(readonly => this.readonlyChange.emit(readonly));
    this.collabSessionDocSizeSubscription = this.collabSession.docSize$.subscribe(docSize => this.sizeChange.emit(docSize));
    this.collabSessionDocCommittedAtSubscription = this.collabSession.docCommittedAt$.subscribe(docCommittedAt => this.docCommittedAtChange.emit(docCommittedAt));
    this.collabSessionDocModifiedAtSubscription = this.collabSession.docModifiedAt$.subscribe(docModifiedAt => this.docModifiedAtChange.emit(docModifiedAt));
    this.collabSessionSyncStateSubscription = this.collabSession.syncState$.subscribe(syncState => this.syncStateChange.emit(syncState));
    this.collabSessionTimelineUpdateSubscription = this.collabSession.timelineUpdate$.subscribe(update => this.timelineUpdate.emit(update));
    this.collabSessionUsersUpdateSubscription = this.collabSession.usersUpdate$.subscribe(users => this.usersUpdate.emit(users));

    // Put the editor into readonly mode if the document can't be saved to MongoDB because its too large for MongoDB
    this.readonly$ = combineLatest([
      this.collabSession.exceedsMongoDBMaxSize$,
      this.collabSession.readOnly$,
      this.readonlyInput$
    ]).pipe(
      map(([exceedsMongoDBMaxSize, readOnly, readonlyInput]) => exceedsMongoDBMaxSize || readOnly || readonlyInput)
    );

    // Notify subscribers that the changes cannot be applied due to certain collab session conditions.
    this.blockChangesSubscription = combineLatest([
      this.collabSession.connected$,
      this.collabSession.loaded$,
      this.collabSession.offline$,
      this.collabSession.stopped$,
      this.collabSession.syncState$,
      this.readonly$
    ]).pipe(
      map(([connected, loaded, offline, stopped, syncState, readonly]) => {
        return !connected || !loaded || offline || stopped || syncState === CollabSessionSyncState.Reverting || readonly;
      }),
      distinctUntilChanged()
    ).subscribe(blockChanges => this.blockChanges.emit(blockChanges));


    // Start the collab session
    this.collabSession.start();

    this.collabSessionStart.emit({
      session: this.collabSession,
      type: CollabSessionEventType.Start
    });
  }

  private showTransactionTooLargeError(event: CollabSessionTransactionTooLargeEvent) {
    this.snackBar.open(`Changes ignored because they exceed ${this.fileSizeService.format(event.maxSize, 0)} limit.`, 'OK');
  }
}
